import React, { useState } from "react";
import useReveal from "@/hooks/useReveal";
import { U } from "@/data/services";
import { insertRow, notifyForm } from "@/lib/forms";
import { useSettings } from "@/lib/SettingsContext";

const HERO_BG = U("1519167758481-83f550bb49b3", 2400, 88);
const DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=24.9839142,55.1215532";
const WAZE_URL = "https://waze.com/ul?ll=24.9839142,55.1215532&navigate=yes";

const IconPhone = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.34 1.78.66 2.62a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6 6l1.46-1.32a2 2 0 0 1 2.11-.45c.84.32 1.72.54 2.62.66A2 2 0 0 1 22 16.92z"/></svg>);
const IconMail = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6L12 13 2 6"/></svg>);
const IconPin = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>);
const IconClock = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const IconGlobe = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>);
const IconTimer = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="12 2 12 6"/><path d="M12 22a10 10 0 1 0-7-17"/><polyline points="12 8 12 13 15 16"/></svg>);

interface FormState { firstName: string; lastName: string; email: string; phone: string; subject: string; message: string; }
const BLANK: FormState = { firstName: "", lastName: "", email: "", phone: "", subject: "", message: "" };

const Contact: React.FC = () => {
  useReveal();
  const { settings } = useSettings();
  const [form, setForm] = useState<FormState>(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setResult(null);
    try {
      await insertRow("contact_messages", {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });
      console.log("[Contact] submission saved, calling notifyForm…");
      notifyForm({
        kind: "contact",
        to: settings.notification_email,
        data: { first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone, subject: form.subject, message: form.message },
      }).then(() => console.log("[Contact] notifyForm resolved")).catch(err => console.error("[Contact] notifyForm failed:", err));
      setResult({ ok: true, msg: "Thank you — your message has been received. We'll be in touch within 24 hours." });
      setForm(BLANK);
    } catch (err: any) {
      setResult({ ok: false, msg: err?.message || "Something went wrong. Please email info@ideationav.com." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="iav-page" data-testid="contact-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>Get In Touch</span>
          <h1 data-reveal>CONTACT<br /><span className="iav-accent">US.</span></h1>
          <p className="lead" data-reveal>Tell us about your event and we'll get back to you with a tailored proposal within 24 hours.</p>
        </div>
      </section>

      <div className="iav-contact-grid">
        <div className="iav-info-list" data-reveal>
          <div className="iav-info-item"><div className="iav-info-icon"><IconPhone /></div><div><div className="iav-info-lbl">Phone</div><div className="iav-info-val"><a href="tel:+97143211919">+971 4 321 1919</a></div></div></div>
          <div className="iav-info-item"><div className="iav-info-icon"><IconMail /></div><div><div className="iav-info-lbl">Email</div><div className="iav-info-val"><a href="mailto:info@ideationav.com">info@ideationav.com</a></div></div></div>
          <div className="iav-info-item"><div className="iav-info-icon"><IconGlobe /></div><div><div className="iav-info-lbl">Website</div><div className="iav-info-val"><a href="https://www.ideationav.com" target="_blank" rel="noopener noreferrer">www.ideationav.com</a></div></div></div>
          <div className="iav-info-item"><div className="iav-info-icon"><IconPin /></div><div><div className="iav-info-lbl">Address</div><div className="iav-info-val">Warehouse 15B DREC,<br />Jabal Ali, Dubai, UAE</div></div></div>
          <div className="iav-info-item"><div className="iav-info-icon"><IconClock /></div><div><div className="iav-info-lbl">Office Hours</div><div className="iav-info-val">Sunday – Thursday<br />9:00 — 18:00 GST</div></div></div>
          <div className="iav-info-item"><div className="iav-info-icon"><IconTimer /></div><div><div className="iav-info-lbl">Response Time</div><div className="iav-info-val">We typically respond within 24 hours</div></div></div>
          <div className="iav-info-dir-row">
            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="iav-btn iav-btn-primary iav-info-dir" data-cursor="cta" data-testid="directions-btn">Google Maps →</a>
            <a href={WAZE_URL} target="_blank" rel="noopener noreferrer" className="iav-btn iav-btn-outline iav-info-dir" data-cursor="cta" data-testid="waze-btn">Waze →</a>
          </div>
        </div>

        <form className="iav-form" onSubmit={onSubmit} data-reveal data-testid="contact-form">
          <div className="iav-form-row">
            <div className="iav-field"><label>First Name *</label><input required className="iav-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} /></div>
            <div className="iav-field"><label>Last Name *</label><input required className="iav-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} /></div>
          </div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Email *</label><input required type="email" className="iav-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="iav-field"><label>Phone</label><input type="tel" className="iav-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="iav-field"><label>Subject *</label>
            <select required className="iav-select" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}>
              <option value="">Select…</option>
              <option>Event Enquiry</option>
              <option>Service Information</option>
              <option>Quote Request</option>
              <option>Partnership</option>
              <option>Other</option>
            </select>
          </div>
          <div className="iav-field"><label>Message *</label><textarea required className="iav-textarea" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Tell us a little about your project…" /></div>
          {result && <div className={`iav-form-msg ${result.ok ? "is-ok" : "is-err"}`} data-testid="contact-result">{result.msg}</div>}
          <button type="submit" disabled={submitting} className="iav-btn iav-btn-primary" data-testid="contact-submit">{submitting ? "Sending…" : "Send Message"}</button>
        </form>
      </div>

      <iframe
        title="Ideation Audio Visual location"
        className="iav-map iav-map-embed iav-map-full"
        src="https://www.google.com/maps?q=24.9839142,55.1215532&z=17&output=embed"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </main>
  );
};
export default Contact;
